import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { useConfirmDraftMemberPackage } from "@/features/Packages/hooks/useConfirmDraftMemberPackage";
import { useIsPackageOrderDue } from "@/features/Packages/hooks/useIsPackageOrderDue";
import { CheckBox, Link } from "@whoosh/design-system/src/components/controls";
import { Button } from "@whoosh/design-system/src/components/controls";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { StoreProductOrderFooter_memberPackage$key } from "./__generated__/StoreProductOrderFooter_memberPackage.graphql";
import { StoreProductOrderFooter_order$key } from "./__generated__/StoreProductOrderFooter_order.graphql";

type Props = {
  memberPackageRef?: StoreProductOrderFooter_memberPackage$key | null;
  orderRef?: StoreProductOrderFooter_order$key | null;
  onSuccessfulPurchase: () => void;
};
export const StoreProductOrderFooter = ({
  memberPackageRef,
  orderRef,
  onSuccessfulPurchase,
}: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const { showToast } = useToast();
  const [termsChecked, setTermsChecked] = useState(false);

  const memberPackage = useFragment(
    StoreProductOrderFooterMemberPackageFragmentString,
    memberPackageRef
  );
  const order = useFragment(
    StoreProductOrderFooterOrderFragmentString,
    orderRef
  );

  const total = order?.total || 0;
  const isPackageOrderDue = useIsPackageOrderDue(order);

  const termsAndConditionsUrl =
    memberPackage?.package?.displayInApp?.termsAndConditionsUrl;

  const squareCardId = memberPackage?.squareCard?.id || null;

  const isPayButtonDisabled =
    (!termsChecked && !!termsAndConditionsUrl) || !squareCardId;

  const [handleConfirmAndPay, isPaying] = useConfirmDraftMemberPackage();

  const onConfirmAndPay = () => {
    if (squareCardId && memberPackage?.id) {
      handleConfirmAndPay(
        {
          paymentMethodId: squareCardId,
          memberPackageId: memberPackage.id,
        },
        () => {
          onSuccessfulPurchase();
        },
        (error) => {
          setTimeout(() => {
            showToast({
              title: t`Error`,
              description: `${t`An Error Occurred while processing your payment`} - ${
                error.message
              }`,
              semanticStyle: "error",
            });
          }, 10);
        }
      );
    }
  };

  return (
    <VStack p={6} gap={4} borderTopColor="surface-dividers" borderTopWidth={1}>
      {termsAndConditionsUrl ? (
        <HStack gap={1} alignItems="center" alignSelf="center">
          <CheckBox
            isChecked={termsChecked}
            onPress={() => setTermsChecked(!termsChecked)}
          />
          <Text>{t`I have read and agree to`}</Text>
          <Link
            href={termsAndConditionsUrl}
            target="_blank"
          >{t`Terms and Conditions`}</Link>
        </HStack>
      ) : null}
      <Button
        accessibilityLabel={t`Confirm and Pay ${currencySymbol}${ConvertCentsToDollars(
          total
        )}`}
        isDisabled={isPayButtonDisabled}
        onPress={onConfirmAndPay}
        isLoading={isPaying}
      >
        {isPackageOrderDue
          ? t`Confirm and Pay ${currencySymbol}${ConvertCentsToDollars(total)}`
          : t`Confirm`}
      </Button>
    </VStack>
  );
};

const StoreProductOrderFooterMemberPackageFragmentString = graphql`
  fragment StoreProductOrderFooter_memberPackage on MemberPackage {
    id
    squareCard {
      id
    }
    package {
      displayInApp {
        termsAndConditionsUrl
      }
    }
  }
`;

const StoreProductOrderFooterOrderFragmentString = graphql`
  fragment StoreProductOrderFooter_order on Order {
    id
    total
    ...useIsPackageOrderDue_order
  }
`;
